import { useTenant, useAnalytics } from '@parcel2go/npm-p2g-app';
import { useCallback, useState } from 'react';
import { AddressLookup, AddressSearchOverview } from '~/lib/types/edge-api-types';
import { addressBreakdown, addressRetrieve, addressSearch } from '~/lib/address-search';
import { AddressSearchSuggest } from './search-suggest';
import { captureException } from '~/lib/metrics';
import { IP2GBasketSection } from '~/hooks/use-basket-section';
import { BasketSections } from '~/lib/types/enums';
import { QuoteAddress } from '~/lib/types/basket-types';

type AddressSearchProps = {
  onSelected: (address: AddressLookup) => void;
  type: BasketSections.ORIGIN | BasketSections.DESTINATION;
  section: IP2GBasketSection<QuoteAddress>;
  errors: Record<string, string>;
  optional?: boolean;
};

const MAX_RESULTS = 50;

export default function AddressSearchCore({ section, type, onSelected, errors, optional }: AddressSearchProps) {
  const { track, session } = useAnalytics();
  const { tenant } = useTenant();
  const [results, setResults] = useState<AddressSearchOverview[]>([]);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const onBreakdown = useCallback(
    async (query: string, address: AddressSearchOverview) => {
      if (!address?.identifier) {
        return;
      }
      try {
        setLoading(true);
        const result = await addressBreakdown(tenant, session, section.data.country, query, address.identifier);
        setResults(result);
        track({
          type: 'address_search_breakdown',
          payload: { identifier: address.identifier, count: result.length || 0, type }
        });
      } catch (e) {
        captureException(e, 'Error in AddressSearch onBreakdown', {
          section: 'checkout'
        });
      } finally {
        setLoading(false);
      }
    },
    [section.data.country, tenant, track, type, session]
  );

  const onRetrieve = useCallback(
    async (address: AddressSearchOverview) => {
      if (!address?.identifier) {
        return;
      }
      try {
        setLoading(true);
        const result = await addressRetrieve(tenant, session, section.data.country, address.identifier);
        setResults([]);
        onSelected(result);
      } catch (e) {
        captureException(e, 'Error in AddressSearch onRetrieve', {
          section: 'checkout'
        });
      } finally {
        setLoading(false);
      }
    },
    [onSelected, section.data.country, tenant, session]
  );

  const onClick = useCallback(
    async (query: string, address: AddressSearchOverview) => {
      if (address.hasBreakdown) {
        await onBreakdown(query, address);
      } else {
        await onRetrieve(address);
      }
    },
    [onBreakdown, onRetrieve]
  );

  const onSearch = useCallback(
    async (query: string, autoSelectSingleResult: boolean = true) => {
      setResults([]);

      if (!query || query.length <= 2) {
        return;
      }

      try {
        setLoading(true);
        setSkip(0);
        const result = await addressSearch(tenant, session, section.data.country, query, 0, MAX_RESULTS);
        setResults(result);
        setHasMore(result.length === MAX_RESULTS);
        track({ type: 'address_search', payload: { query, count: result.length, type } });
        if (autoSelectSingleResult && result.length === 1) {
          onClick(query, result[0]);
          return;
        }
      } catch (e) {
        captureException(e, 'Error in AddressSearch onSearch', {
          section: 'checkout'
        });
      } finally {
        setLoading(false);
      }
    },
    [onClick, section.data.country, tenant, track, type, session]
  );

  const onLoadMore = useCallback(
    async (query: string) => {
      const result = await addressSearch(tenant, session, section.data.country, query, skip + MAX_RESULTS, MAX_RESULTS);
      setResults((prev) => [...prev, ...result]);
      setSkip((prevSkip) => prevSkip + MAX_RESULTS);
      setHasMore(result.length === MAX_RESULTS);
    },
    [section.data.country, tenant, session, skip]
  );

  return (
    <AddressSearchSuggest
      initial={section?.data?.postcode}
      optional={optional}
      type={type}
      results={results}
      onSearch={onSearch}
      onClick={onClick}
      loading={loading}
      errors={errors}
      onLoadMore={onLoadMore}
      hasMore={hasMore}
    />
  );
}
